import React, { forwardRef, memo } from 'react'
import Icon, { IconNode, IconProps } from './Icon'

/**
 * Helper function to convert string to kebab case
 */
export const toKebabCase = (str: string): string => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/\s+/g, '-')
    .toLowerCase()
}

/**
 * Helper function to merge CSS classes
 */
export const mergeClasses = (...classes: (string | undefined)[]): string => {
  return classes.filter(Boolean).join(' ')
}

/**
 * Create an icon component from an icon name and node
 * @param iconName The name of the icon
 * @param iconNode The SVG node data
 * @returns A React component for the icon
 */
export const createIcon = (
  iconName: string,
  iconNode: IconNode,
  viewBox?: string,
  width?: number | string,
  height?: number | string
) => {
  // Define props type without the iconNode prop which is provided internally
  type IconComponentProps = Omit<IconProps, 'iconNode'>

  const Component = forwardRef<SVGSVGElement, IconComponentProps>(
    ({ className, ...props }, ref) => (
      <Icon
        ref={ref}
        iconNode={iconNode}
        viewBox={viewBox}
        width={width}
        height={height}
        className={mergeClasses(
          `custom-icon-${toKebabCase(iconName)}`,
          className
        )}
        {...props}
      />
    )
  )

  Component.displayName = iconName

  return memo(Component)
}

export default createIcon
